/**
 * OpenTelemetry messaging semantic-convention keys for hand-rolled messaging spans
 * (e.g. Socket.IO PRODUCER/CONSUMER spans). The messaging semconv is experimental, so
 * these names can change between semconv releases.
 *
 * Lives here rather than in notability-services-common-tracing so the web client and
 * the collab server emit byte-identical keys; that package depends on
 * `@opentelemetry/sdk-node` and can't be imported from the browser bundle.
 *
 * The `socketIo*` keys mirror `SocketIoInstrumentationAttributes` from
 * `@opentelemetry/instrumentation-socket.io`, which collab-api registers, so hand-rolled
 * and auto-instrumented spans stay queryable together.
 *
 * @see https://opentelemetry.io/docs/specs/semconv/messaging/messaging-spans/
 */
export const MessagingSpanAttrs = {
  system: 'messaging.system',
  operationType: 'messaging.operation.type',
  destinationName: 'messaging.destination.name',
  socketIoNamespace: 'messaging.socket.io.namespace',
  socketIoEventName: 'messaging.socket.io.event_name'
} as const

/**
 * Collab span attribute keys emitted from both the browser and collab-api. They live
 * here, alongside {@link MessagingSpanAttrs} and duplicating the matching entries in
 * notability-services-common-tracing's `SpanAttrs`, for the same reason: that package
 * depends on `@opentelemetry/sdk-node` and cannot be imported from the browser bundle.
 *
 * Keep the values byte-identical to their `SpanAttrs` counterparts — a rename on one
 * side alone splits a Honeycomb column in two. A key only one side emits does not
 * belong here; it belongs next to the code that sets it.
 */
export const CollabSpanAttrs = {
  metadataMutationCount: 'collab.metadata.mutation_count'
} as const
